import { OrderBookLadder } from "../types/market";
import { OrderBookRows, type OrderBookDisplayRow } from "./OrderBookRows";
import { buildDepthRows, type LadderLevel } from "./marketDepth";

type OrderBookLadderProps = {
  ladder: OrderBookLadder | null;
  isLoading: boolean;
  isError: boolean;
  teamCode: string;
  topBidOrderId?: number | null;
  topAskOrderId?: number | null;
  showSideLabelColumns?: boolean;
};

export function OrderBookLadderView({
  ladder,
  isLoading,
  isError,
  teamCode,
  topBidOrderId = null,
  topAskOrderId = null,
  showSideLabelColumns = false,
}: OrderBookLadderProps) {
  const containerClass =
    "w-full overflow-hidden rounded-b-md border border-border-subtle/30 border-t-0 bg-background-raised/60 text-body transition-all duration-200";
  const messageClass = "px-3 py-2 text-sm text-foreground-muted";

  if (isLoading) {
    return (
      <div className={containerClass}>
        <div className={messageClass}>Loading order book...</div>
      </div>
    );
  }

  if (isError || !ladder) {
    return (
      <div className={containerClass}>
        <div className="px-3 py-2 text-sm text-danger">Failed to load order book.</div>
      </div>
    );
  }

  const { bid, ask } = ladder;
  const hasData = bid.length > 0 || ask.length > 0;

  if (!hasData) {
    return (
      <div className={containerClass}>
        <div className={messageClass}>No open orders for {teamCode}.</div>
      </div>
    );
  }

  const rows: OrderBookDisplayRow[] = buildDepthRows(
    bid as LadderLevel[],
    ask as LadderLevel[],
    {
      topBidOrderId,
      topAskOrderId,
    },
  ).map((row, index) => ({
    key: `book-row-${index}`,
    bid: row.bid
      ? {
          trader: row.bid.trader_code,
          qty: String(row.bid.quantity),
          price: row.bid.price.toFixed(0),
          hasValue: true,
        }
      : {
          trader: "—",
          qty: "—",
          price: "—",
          hasValue: false,
        },
    ask: row.ask
      ? {
          trader: row.ask.trader_code,
          qty: String(row.ask.quantity),
          price: row.ask.price.toFixed(0),
          hasValue: true,
        }
      : {
          trader: "—",
          qty: "—",
          price: "—",
          hasValue: false,
        },
  }));

  return (
    <div className={containerClass}>
      {rows.length === 0 ? (
        <div className={messageClass}>No additional depth.</div>
      ) : (
        <OrderBookRows rows={rows} withRowBorders showSideLabelColumns={showSideLabelColumns} />
      )}
    </div>
  );
}
